import React, { useEffect, useMemo, useState } from "react";
import { useRoutes } from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import "./i18n";
import createTheme from "./theme";
import AuthGuard from "./components/guards/AuthGuard"; //BY Abubbakr
import createRoutes from "./routes";

import useTheme from "./hooks/useTheme";
import useAuth from "./hooks/useAuth";
import { store } from "./redux/store";
import createEmotionCache from "./utils/createEmotionCache";

import { AuthProvider } from "./contexts/AMAuthContext";
import { MenuProvider } from "./contexts/MenuContext";
import { GetUserMenu } from "./services/generalService";
//import { AuthProvider } from "./contexts/JWTContext";
// import { AuthProvider } from "./contexts/FirebaseAuthContext";
// import { AuthProvider } from "./contexts/Auth0Context";
// import { AuthProvider } from "./contexts/CognitoContext";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

const clientSideEmotionCache = createEmotionCache();

// Helper function to extract dynamic routes
const extractDynamicRoutes = (menuItems) => {
  const result = [];
  const seen = new Set();

  const getRouteCodeFromHref = (href) => {
    if (typeof href !== "string" || !href.trim()) {
      return "";
    }

    const normalizedHref = href.split("?")[0].replace(/\/$/, "");
    const segments = normalizedHref.split("/").filter(Boolean);

    return segments.length ? segments[segments.length - 1] : "";
  };

  const visit = (items) => {
    if (!Array.isArray(items)) {
      return;
    }

    items.forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }

      if (item.componentName) {
        const routeCode = getRouteCodeFromHref(item.href);

        if (routeCode) {
          const uniqueKey = `${routeCode}::${item.componentName}`;

          if (!seen.has(uniqueKey)) {
            seen.add(uniqueKey);
            result.push({
              routeCode,
              componentName: item.componentName,
            });
          }
        }
      }

      visit(item.pages);
      visit(item.children);
    });
  };

  visit(menuItems);

  return result;
};

// Inner component that uses the auth context (must be inside AuthProvider)
function AppContent() {
  const [userMenu, setUserMenu] = useState([]);
  const [dynamicRoutes, setDynamicRoutes] = useState([]);
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    let isMounted = true;
    const loadUserMenu = async () => {
      try {
        const { data } = await GetUserMenu();
        if (isMounted) {
          setUserMenu(Array.isArray(data) ? data : []);
          setDynamicRoutes(extractDynamicRoutes(Array.isArray(data) ? data : []));
        }
      } catch (error) {
        if (isMounted) {
          setUserMenu([]);
        }
      }
    };

    // Only load menu when user is authenticated
    if (isAuthenticated) {
      loadUserMenu();
    } else {
      // Reset menu when user logs out
      setUserMenu([]);
      setDynamicRoutes([]);
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const routes = useMemo(
    () =>
      createRoutes({
        dynamicRoutes,
      }),
    [dynamicRoutes]
  );

  const content = useRoutes(routes);

  return (
    <>
      <MuiThemeProvider theme={createTheme(theme)}>
        <MenuProvider value={{ userMenu }}>
          <AuthGuard>{content}</AuthGuard>
        </MenuProvider>
      </MuiThemeProvider>
    </>
  );
}

// Main App component that sets up all providers
function App({ emotionCache = clientSideEmotionCache }) {
  return (
    <>
      <ToastContainer />
      <CacheProvider value={emotionCache}>
        <HelmetProvider>
          <Helmet titleTemplate="%s | Click ERP" defaultTitle="Click ERP" />
          <Provider store={store}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            </LocalizationProvider>
          </Provider>
        </HelmetProvider>
      </CacheProvider>
    </>
  );
}

export default App;
